import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Typography,
} from "@mui/material";

/*
  Reusable guarded Edit button.

  Props:
    row                : record object (optional, just passed through to onEdit)
    isApproved         : boolean (true -> show confirmation dialog)
    onEdit             : () => void | Promise<void>  (actual edit logic)
    disabled           : boolean (normal MUI disabled)
    label              : button text (default "Edit")
    dialogTitle        : optional override
    message            : optional override for body text
    confirmLabel       : optional override (default "Continue")
    cancelLabel        : optional override (default "Cancel")
    requireReapproval  : boolean (if true adds a second line about re-approval; default true)
*/
export default function ApprovedEditButton({
  row,
  isApproved,
  onEdit,
  disabled = false,
  label = "Edit",
  dialogTitle = "Editing Approved Record",
  message = "This record is approved. Editing it will require re-approval after you save.",
  confirmLabel = "Continue",
  cancelLabel = "Cancel",
  requireReapproval = true,
  size,
  variant,
  ...buttonProps
}) {
  const [open, setOpen] = useState(false);
  const handleClick = (e) => {
    e.stopPropagation();
    if (disabled) return;
    if (isApproved) {
      setOpen(true);
    } else {
      onEdit?.(row);
    }
  };
  const proceed = () => {
    setOpen(false);
    onEdit?.(row);
  };
  return (
    <>
      <Button
        onClick={handleClick}
        disabled={disabled}
        size={size || "medium"}
        variant={variant || "text"}
        {...buttonProps}
      >
        {label}
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Typography variant="body2">{message}</Typography>
            {requireReapproval && (
              <Typography variant="caption" color="text.secondary">
                After saving, it must be approved again.
              </Typography>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{cancelLabel}</Button>
          <Button onClick={proceed} autoFocus>
            {confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}